/**
 * The ONE landing helper of the tenant management SPA.
 *
 * Every redirect whose target is a registrable surface (the agent-shell index,
 * each agent nav group, the workspace index and each workspace nav group)
 * resolves here instead of naming a literal path. The answer is the FIRST
 * candidate, in declared order, that THIS caller can use; when none is usable the
 * answer is `null` and the caller renders no redirect at all
 * (SPEC-TENANT-SURFACES-RULE-005(b)).
 *
 * Why it exists: the agent index used to redirect to the literal
 * `settings/general`, which requires `workspace.settings.manage` - held only by
 * platform_admin and tenant_admin (internal/api/middleware/authz.go
 * FrontendPermissions) - so operator, user and viewer, three of the five roles,
 * landed on the Access Denied panel. RULE-005 names that a lockout.
 *
 * Pure functions, no React. The `usable` predicate is INJECTED: in W0 it was the
 * release gate AND the caller's frontend permission (the inputs the routes
 * already gate on - see `w0Usable`). Since TASK-TSURF-W3-LANDING-RESOLVER the
 * LIVE predicate is `resolvedUsable`, which adds the resolved surface set, so a
 * landing can no longer resolve to a route the builders did not register: once
 * the W3 nav/route filter landed, a group redirect resolved on permission and
 * release alone could send the caller to a hidden sibling, and the caller
 * arrived on the not-found page (RULE-005(b), RULE-006 (2)). `w0Usable` stays
 * as the release+permission half `resolvedUsable` is built from, and as the
 * predicate landing.test.ts measures the regression against.
 *
 * This module still imports NOTHING from `web/src/surfaces/`: a candidate names
 * its registry surface key as a plain string and the "is this key usable for
 * this caller" answer arrives injected, so every function here stays callable
 * from a test without React, a QueryClient, or a client copy of the registry
 * (RULE-016).
 *
 * Candidate paths are RELATIVE TO THE ROUTE THAT REDIRECTS (the agent index sits
 * at the shell root, the `settings` group route at `settings`, and so on), so a
 * caller hands the answer straight to `<Navigate to=...>`. `resolveTenantLanding`
 * is the one exception and says so: it crosses out of the workspace layout into
 * the agent shell, so it answers in ABSOLUTE paths.
 *
 * @module apps/tenant/landing
 * @see SPEC-TENANT-SURFACES-RULE-005
 * @see EPIC-TENANT-SURFACES-AC-022 (amended by D-375)
 * @see TASK-TSURF-W0-SHELL-NOTFOUND-AND-LANDING-CHG-001
 * @see TASK-TSURF-W3-LANDING-RESOLVER-CHG-001
 */
// Type-only: erased at build time, so this module still pulls in no runtime
// dependency. It is the ONE declaration of the posture contract (see
// LandingPosture below), never a second fetcher and never a copy.
import type { AgentPosture } from "../../api/agentPosture";

/** One child a landing may resolve to, with the gates that guard its route today. */
export interface LandingCandidate {
  /** Path relative to the redirecting route. */
  path: string;
  /** Canonical release-toggle key gating the route, when it has one. */
  feature?: string;
  /** Frontend permission the route's PermissionGate / ProtectedRoute requires. */
  permission?: string;
  /**
   * The registry surface key of the route this path mounts
   * (SPEC-TENANT-SURFACES-RULE-017), the SAME key `routes.tsx` declares on that
   * route. Typed as a plain string, not imported from `surfaces/`, for the
   * reason the module comment gives. `undefined` means the mount has no
   * registry row and is therefore not surface-gated - the same leg
   * `surfaceRouteUsable` spells `null` in routes.tsx.
   */
  surface?: string;
}

/** Decides whether THIS caller can use a candidate. Injected, never hardcoded. */
export type UsablePredicate = (candidate: LandingCandidate) => boolean;

/** The nav groups of the agent-detail shell that own a landing. */
export type AgentLandingGroup = "index" | "settings" | "sources" | "training";

/** The nav groups of the workspace layout that own a landing. */
export type WorkspaceLandingGroup = "index" | "sources" | "settings";

/** What a landing call needs to know about the caller. */
export interface LandingContext<G extends string> {
  group: G;
  usable: UsablePredicate;
}

const SETTINGS_MANAGE = "workspace.settings.manage";
const AGENTS_MANAGE = "workspace.agents.manage";
const SOURCES_MANAGE = "workspace.sources.manage";

// The agent pages more than one list below names, declared once so "the
// Settings landing" is one object wherever it appears.
const AGENT_SETTINGS_CANDIDATE: LandingCandidate = {
  path: "settings/general",
  permission: SETTINGS_MANAGE,
  surface: "agent.settings.general",
};
const AGENT_PLAYGROUND_CANDIDATE: LandingCandidate = { path: "playground", surface: "agent.playground" };
const AGENT_ANALYTICS_CANDIDATE: LandingCandidate = { path: "analytics", surface: "agent.analytics" };
const AGENT_TRAINING_CANDIDATE: LandingCandidate = {
  path: "training",
  permission: AGENTS_MANAGE,
  surface: "agent.training.index",
};

/**
 * Agent-shell landing candidates, in declared order. The order mirrors the
 * gates in routes.tsx. The index keeps `settings/general` FIRST so the two
 * admin roles land exactly where they did before; every other role falls through
 * to Playground, which carries no permission and no release toggle and is the
 * first item of the agent sidebar. Plugin-kind-only children (settings/ai,
 * settings/task, chat-logs, report-history) are not candidates: the landing does
 * not know the agent's plugin kind, and Playground covers both kinds.
 *
 * The index is the DEFAULT order: a platform admin's agent start page
 * (D-TSG-32, `agentIndexCandidates`) is put in front of it and never replaces
 * it, so a start page this caller cannot use falls through to exactly this list.
 */
export const AGENT_LANDING_CANDIDATES: Readonly<Record<AgentLandingGroup, readonly LandingCandidate[]>> = {
  index: [AGENT_SETTINGS_CANDIDATE, AGENT_PLAYGROUND_CANDIDATE, AGENT_ANALYTICS_CANDIDATE],
  // Relative to the `settings` route.
  settings: [
    { path: "general", permission: SETTINGS_MANAGE, surface: "agent.settings.general" },
    { path: "knowledge", permission: SETTINGS_MANAGE, surface: "agent.settings.knowledge" },
    { path: "security", permission: SETTINGS_MANAGE, surface: "agent.settings.security" },
  ],
  // Relative to the `sources` route (the legacy arm; the unified arm serves a
  // page at `sources` and redirects nothing to a surface).
  sources: [
    { path: "files", permission: SOURCES_MANAGE, surface: "agent.sources.files" },
    { path: "website", feature: "agent.sources.website", permission: SOURCES_MANAGE, surface: "agent.sources.website" },
    { path: "schemas", feature: "agent.metadata", permission: SOURCES_MANAGE, surface: "agent.sources.schemas" },
  ],
  // Relative to the agent SHELL: `training` is itself a page, so no redirect
  // route mounts this group today. It is declared so the nav group W1 generates
  // resolves through the same helper instead of a literal.
  training: [
    AGENT_TRAINING_CANDIDATE,
    { path: "training/schedules", feature: "agent.schedules", permission: AGENTS_MANAGE, surface: "agent.training.schedules" },
    { path: "training/job-history", permission: AGENTS_MANAGE, surface: "agent.training.job-history" },
  ],
};

/**
 * The agent chooser, the one landing every posture may fall back to: the
 * workspace agent list. Its registry row is `configurable: false` (RULE-005(a)),
 * so a tenant cannot hide it and this fallback cannot be configured away - but
 * it is still ASKED, never assumed, because the resolved set is the only
 * authority on what this caller may use (RULE-003).
 *
 * The SAME object is the first workspace-index candidate below, so "is the
 * chooser reachable" is one question with one answer in both places.
 */
export const CHOOSER_CANDIDATE: LandingCandidate = { path: "agents", surface: "tenant.agents" };

/** Absolute path of the chooser - what `resolveTenantLanding` falls back to. */
export const CHOOSER_PATH = "/agents";

/**
 * The tenant members page (`settings/members`). "Can this caller manage the
 * tenant's members" is asked of THIS candidate through the injected predicate -
 * its permission, and the resolved surface - never of a role name (D-SCA-10,
 * `resolveLogoTarget`). The SAME object is the `settings` group's members entry
 * below, so the logo and the settings redirect answer the question once.
 */
export const MEMBERS_CANDIDATE: LandingCandidate = {
  path: "members",
  permission: "workspace.members.manage",
  surface: "tenant.settings.members",
};

/** Workspace-layout landing candidates, in declared order. */
export const WORKSPACE_LANDING_CANDIDATES: Readonly<Record<WorkspaceLandingGroup, readonly LandingCandidate[]>> = {
  index: [
    CHOOSER_CANDIDATE,
    { path: "usage", surface: "tenant.usage" },
    { path: "features", surface: "tenant.features" },
  ],
  // Relative to the `sources` route.
  sources: [
    { path: "files", feature: "tenant.sources.files", surface: "tenant.sources.files" },
    { path: "website", feature: "tenant.sources.website", surface: "tenant.sources.website" },
  ],
  // Relative to the `settings` route.
  settings: [
    { path: "general", permission: SETTINGS_MANAGE, surface: "tenant.settings.general" },
    MEMBERS_CANDIDATE,
    { path: "roles", permission: "workspace.roles.manage", surface: "tenant.settings.roles" },
    { path: "permissions", feature: "tenant.settings.permissions", permission: "workspace.roles.manage", surface: "tenant.settings.permissions" },
    { path: "plugins", feature: "tenant.settings.plugins", permission: "workspace.plugins.manage", surface: "tenant.settings.plugins" },
    { path: "schemas", feature: "tenant.settings.schemas", permission: SETTINGS_MANAGE, surface: "tenant.settings.schemas" },
    // Not answer-provenance: its URL sits under settings/, but it is a
    // top-level entry outside this group (D-TSG-17, SPEC-FRONTEND-UI-058), so
    // the settings landing never picks it.
  ],
};

/**
 * The first usable candidate's path, in declared order, or `null` when none is
 * usable (the caller then renders no redirect - RULE-005(b)).
 */
export function firstUsable(
  candidates: readonly LandingCandidate[],
  usable: UsablePredicate,
): string | null {
  for (const candidate of candidates) {
    if (usable(candidate)) return candidate.path;
  }
  return null;
}

/**
 * Landing target for an agent-shell group (the index and each nav group).
 * `startKey` is the agent start page that applies to this agent
 * (`agentStartKey`); it reorders the INDEX only, and is ignored by every other
 * group.
 */
export function firstUsableAgentChild(
  ctx: LandingContext<AgentLandingGroup> & { startKey?: string | null },
): string | null {
  const candidates =
    ctx.group === "index" ? agentIndexCandidates(ctx.startKey ?? null) : AGENT_LANDING_CANDIDATES[ctx.group];
  return firstUsable(candidates, ctx.usable);
}

// ---------------------------------------------------------------------------
// The agent start page (D-TSG-32)
// ---------------------------------------------------------------------------

/**
 * The pages a platform admin may name as where an agent opens: the agent
 * sidebar's own top-level pages. The gateway refuses any other key on save
 * (internal/surfaces/agent_start.go `agentStartSurfaces`), and
 * TestAgentStartSurfaces_MatchTheTypeScriptTwin compares the two lists as text,
 * so a key added on one side only fails a Go test.
 */
export const AGENT_START_KEYS = [
  "agent.playground",
  "agent.analytics",
  "agent.chat-logs",
  "agent.deploy",
  "agent.training",
  "agent.sources",
  "agent.settings",
  "agent.knowledge",
] as const;

/** One of {@link AGENT_START_KEYS}. */
export type AgentStartKey = (typeof AGENT_START_KEYS)[number];

/**
 * Where each start page opens, relative to the agent shell, with the gates its
 * route carries (routes.tsx) - so a start page is asked of the SAME predicate
 * as every other landing and is skipped, never forced, when this caller cannot
 * use it. `agent.settings` opens its General page, as the default index does.
 * `agent.sources` opens `sources`: the merged list where agent.sources.unified
 * is released, the group redirect where it is not.
 */
const AGENT_START_TARGETS: Readonly<Record<AgentStartKey, LandingCandidate>> = {
  "agent.playground": AGENT_PLAYGROUND_CANDIDATE,
  "agent.analytics": AGENT_ANALYTICS_CANDIDATE,
  "agent.chat-logs": { path: "chat-logs", surface: "agent.chat-logs" },
  "agent.deploy": { path: "deploy", permission: AGENTS_MANAGE, surface: "agent.deploy" },
  "agent.training": AGENT_TRAINING_CANDIDATE,
  "agent.sources": { path: "sources", permission: SOURCES_MANAGE, surface: "agent.sources" },
  "agent.settings": AGENT_SETTINGS_CANDIDATE,
  "agent.knowledge": { path: "knowledge", permission: AGENTS_MANAGE, surface: "agent.knowledge" },
};

function isAgentStartKey(key: string): key is AgentStartKey {
  return (AGENT_START_KEYS as readonly string[]).includes(key);
}

/**
 * The platform admin's agent start page as `GET /api/v1/me/surfaces` answered
 * it (`agent_start_surface`, `agent_start_overrides`). The server lists only a
 * key this caller can use; `null` wherever there is no successful answer.
 */
export interface AgentStartConfig {
  /** The tenant default, or null when none is chosen. */
  tenantDefault: string | null;
  /** Per-agent exceptions: agent id -> key. */
  perAgent: Readonly<Record<string, string>>;
}

/**
 * The start key that applies to `agentId`: its own exception, else the tenant
 * default, else `null` (the default order). No config - loading, failed, or an
 * older server - is `null`, never a guess.
 */
export function agentStartKey(
  config: AgentStartConfig | null | undefined,
  agentId: string | null | undefined,
): string | null {
  if (!config) return null;
  if (agentId && Object.prototype.hasOwnProperty.call(config.perAgent, agentId)) {
    return config.perAgent[agentId];
  }
  return config.tenantDefault;
}

/**
 * The agent index candidates with the start page FIRST and the default order
 * after it (without a second copy of the same path). An unknown key - one a
 * newer server knows and this bundle does not - is ignored, so the default
 * order answers.
 */
export function agentIndexCandidates(startKey: string | null): readonly LandingCandidate[] {
  if (startKey === null || !isAgentStartKey(startKey)) return AGENT_LANDING_CANDIDATES.index;
  const start = AGENT_START_TARGETS[startKey];
  return [start, ...AGENT_LANDING_CANDIDATES.index.filter((c) => c.path !== start.path)];
}

/** Landing target for a workspace-layout group (the index and each nav group). */
export function firstUsableWorkspaceChild(ctx: LandingContext<WorkspaceLandingGroup>): string | null {
  return firstUsable(WORKSPACE_LANDING_CANDIDATES[ctx.group], ctx.usable);
}

/**
 * The W0 predicate: a candidate is usable when its release toggle (if any) is
 * enabled AND the caller holds its permission (if any) - the same two gates the
 * route itself applies, so a landing never points at a route that would deny.
 */
export function w0Usable(inputs: {
  isEnabled: (feature: string) => boolean;
  hasPermission: (permission: string) => boolean;
}): UsablePredicate {
  return (candidate) =>
    (candidate.feature === undefined || inputs.isEnabled(candidate.feature)) &&
    (candidate.permission === undefined || inputs.hasPermission(candidate.permission));
}

/**
 * THE LIVE PREDICATE (TASK-TSURF-W3-LANDING-RESOLVER-CHG-001): the two W0 gates
 * AND the resolved surface set.
 *
 * `isSurfaceUsable` is `useSurfaceSet().isUsable` - the same answer the route
 * builders filter on (`RouteSurfaceFilter`), injected rather than imported. That
 * identity is the whole point: the builders OMIT the `<Route>` of a surface this
 * caller cannot use, so a landing resolved without the set could name a sibling
 * that is no longer registered and drop the caller on the not-found page. It
 * fails closed by construction - while the set is loading and on its error path
 * `isUsable` is false for every key but the ones the server pinned (RULE-014).
 */
export function resolvedUsable(inputs: {
  isEnabled: (feature: string) => boolean;
  hasPermission: (permission: string) => boolean;
  isSurfaceUsable: (surface: string) => boolean;
}): UsablePredicate {
  const gated = w0Usable(inputs);
  return (candidate) =>
    gated(candidate) &&
    (candidate.surface === undefined || inputs.isSurfaceUsable(candidate.surface));
}

// ---------------------------------------------------------------------------
// The tenant landing chain (EPIC-TENANT-SURFACES-AC-022, amended by D-375)
// ---------------------------------------------------------------------------

/** The two landing values of SPEC-GO-API-RULE-049 / RULE-051. */
/**
 * What the resolver reads of `GET /api/v1/me/agent-posture`
 * (SPEC-GO-API-CONTRACT-047, amended by D-375): the six landing fields of
 * `AgentPosture`, NARROWED FROM the owning type rather than restated.
 *
 * A `Pick` of a type-only import, so there is exactly one declaration of the
 * contract in the SPA: `api/agentPosture` stays the ONE owner of the type, the
 * key and the fetcher (SPEC-FRONTEND-RULE-077, QUERY-KEY-GATE), and a rename or
 * a dropped field on the server side becomes a COMPILE error here instead of an
 * `undefined` that quietly sends every caller to the chooser. The import is
 * erased at build time, so this module stays pure: no axios, no QueryClient, no
 * React, and a test still calls the resolver with an object literal.
 *
 * `may_create` is deliberately NOT picked - the create affordance is not a
 * landing question. (The agents MENU entry does read it, through its own
 * `AgentsMenuPosture` below: the 2026-10-05 owner instruction ties the entry to
 * whether the caller may create another agent. `resolveTenantLanding` itself
 * is unchanged, but its EFFECT at login is not: when it answers the chooser -
 * a resolved `chooser`, whether the tenant default or the caller's own per-user
 * row (D-375 F1) - the index redirect lands on `/agents`, whose route REPLACES
 * the visit through `resolveChooserRedirect`. So for a caller with one agent
 * who may not create another and cannot manage members, a `chooser` landing now
 * ends inside that agent; a members manager stays on the chooser (D-SCA-10).
 * agentsMenuSingle.test.tsx "the login landing" pins it.)
 */
export type LandingPosture = Pick<
  AgentPosture,
  | "multi_agent_allowed"
  | "agent_landing"
  | "agent_landing_id"
  | "agent_landing_source"
  | "tenant_agent_landing"
  | "tenant_agent_landing_id"
>;

/** One agent of the tenant, as the chain needs it: an id it can verify. */
export interface LandingAgent {
  id: string;
}

/**
 * Everything the chain resolves against. `posture` and `agents` are `null`
 * whenever the read behind them has no answer - a 503, an error state, a fetch
 * still in flight - and a `null` is FAIL-CLOSED, never a guess.
 */
export interface TenantLandingInputs {
  posture: LandingPosture | null;
  agents: readonly LandingAgent[] | null;
  /** `resolvedUsable` in the app; the W0 predicate only in the regression test. */
  usable: UsablePredicate;
  /** Where inside the agent it opens (D-TSG-32); absent or null = the default order. */
  agentStart?: AgentStartConfig | null;
}

/**
 * Where inside `agentId` this caller lands, or `null` when they cannot land
 * there at all.
 *
 * Both halves are decided HERE, at resolve time, and neither is trusted from
 * the stored row: the id must still name an agent of THIS tenant (AgentLayout
 * swallows a failed getAgent, so a deleted or inaccessible id would render a
 * broken shell rather than an error), and the agent shell must have at least
 * one usable child - never the literal `settings/general`, which is the lockout
 * RULE-005 exists to prevent.
 */
function insideAgent(
  agentId: string | null | undefined,
  agents: readonly LandingAgent[],
  usable: UsablePredicate,
  agentStart: AgentStartConfig | null | undefined,
): string | null {
  if (agentId === null || agentId === undefined || agentId === "") return null;
  if (!agents.some((a) => a.id === agentId)) return null;
  const child = firstUsableAgentChild({ group: "index", usable, startKey: agentStartKey(agentStart, agentId) });
  if (child === null) return null;
  return `${CHOOSER_PATH}/${agentId}/${child}`;
}

/**
 * The workspace landing of THIS caller: an ABSOLUTE path, or `null` when not
 * even the chooser is usable (the caller then renders no redirect - RULE-005(b)).
 *
 * The chain (EPIC-TENANT-SURFACES-AC-022 as amended by D-375, SPEC-GO-API
 * RULE-049: "falls back user -> tenant default -> agent list"):
 *
 *  1. A CAPPED tenant holding exactly one agent lands INSIDE it. D-375 F4 keeps
 *     per-user overrides while a `single_agent` row exists but does not apply
 *     them, and a chooser listing one agent is a dead click.
 *  2. A resolved `chooser` goes to the chooser DIRECTLY and never falls through
 *     to the tenant default (D-375: it is a choice, not a failure).
 *  3. The resolved agent, when it is still an agent of this tenant and has a
 *     usable surface.
 *  4. Only when the resolved landing came from the caller's OWN row: the tenant
 *     default, under the same two tests. A default that is itself `chooser`
 *     ends the chain at the chooser.
 *  5. Otherwise the chooser.
 *
 * Every other case - no posture (503 or error), no agent list, zero agents, a
 * deleted or inaccessible id, an agent whose every child surface is hidden -
 * lands on the chooser. There is no guessed landing anywhere in this function.
 */
export function resolveTenantLanding(inputs: TenantLandingInputs): string | null {
  const { posture, agents, usable, agentStart } = inputs;
  const chooser = usable(CHOOSER_CANDIDATE) ? CHOOSER_PATH : null;

  // Fail closed: a failed or unresolved read is never a landing (step 5).
  if (posture === null || agents === null) return chooser;

  // 1. The capped single-agent tenant.
  if (!posture.multi_agent_allowed && agents.length === 1) {
    return insideAgent(agents[0].id, agents, usable, agentStart) ?? chooser;
  }

  // 2. A resolved chooser is the answer, not a step.
  if (posture.agent_landing !== "agent") return chooser;

  // 3. The landing resolved for this caller.
  const resolved = insideAgent(posture.agent_landing_id, agents, usable, agentStart);
  if (resolved !== null) return resolved;

  // 4. The tenant default, but only under a per-user row: when the gateway
  //    already resolved the tenant default, legs 3 and 4 are the same agent.
  if (posture.agent_landing_source === "user" && posture.tenant_agent_landing === "agent") {
    const fallback = insideAgent(posture.tenant_agent_landing_id, agents, usable, agentStart);
    if (fallback !== null) return fallback;
  }

  // 5. The agent list.
  return chooser;
}

// ---------------------------------------------------------------------------
// The agents menu entry with one agent and no create (owner, 2026-10-05)
// ---------------------------------------------------------------------------

/**
 * What the agents menu entry reads of `GET /api/v1/me/agent-posture`: the ONE
 * field `LandingPosture` deliberately leaves out. Narrowed from the owning type
 * for the reason `LandingPosture` gives - a rename on the server side is a
 * compile error here, never an `undefined` that silently reads as "may not".
 */
export type AgentsMenuPosture = Pick<AgentPosture, "may_create">;

/** Everything the agents menu entry resolves against; `null` = no answer (fail closed). */
export interface AgentsMenuInputs {
  posture: AgentsMenuPosture | null;
  agents: readonly LandingAgent[] | null;
  /** `resolvedUsable` in the app, through `useLandingPredicate`. */
  usable: UsablePredicate;
  /** Where inside the agent it opens (D-TSG-32); absent or null = the default order. */
  agentStart?: AgentStartConfig | null;
}

/**
 * Where the agents menu entry leads THIS caller when the entry stands for ONE
 * agent: an ABSOLUTE path inside it, or `null` for "as before" - the plural
 * label and the agent chooser.
 *
 * The owner, 2026-10-05 08:49:17Z: "Ha valakinek csak egy ügynöke van és nincs
 * neki engedélyezve, hogy létrehozzon másik ügynököt, akkor az "Ügynökök"
 * menüpont kiválasztásakor mindenképpen abba az ügynökbe lépjen bele
 * automatikusan." And at 08:50:31Z, on the label and the exception: the
 * singular word, straight into the agent - "Kivéve ha engedélyezve van a
 * létrehozás, mert akkor ugye ott tudna létrehozni mégegy ügynököt, így akkor
 * fel kell hoznunk neki az ügynökválasztót."
 *
 * So the entry stands for one agent only when ALL of these hold, each asked of
 * a SETTLED read and never guessed:
 *  - the posture answered and says `may_create` is exactly `false` (a caller who
 *    may create keeps the chooser, where the create control lives, whatever the
 *    count; a missing posture - 503, 403, in flight - is not a "no");
 *  - the caller's agent list answered and holds exactly one agent;
 *  - that agent has a usable child surface - the SAME `insideAgent` test, and so
 *    the same target, the landing chain lands on (never the literal
 *    `settings/general`, RULE-005).
 */
export function resolveSingleAgentTarget(inputs: AgentsMenuInputs): string | null {
  const { posture, agents, usable, agentStart } = inputs;
  if (posture === null || agents === null) return null;
  if (posture.may_create !== false) return null;
  if (agents.length !== 1) return null;
  return insideAgent(agents[0].id, agents, usable, agentStart);
}

/**
 * Where a visit of the chooser route itself (`/agents`, typed or reached from
 * a not-found page's home button) is REPLACED to, or `null` to render the
 * chooser.
 *
 * It is `resolveSingleAgentTarget` with ONE carve-out, and the carve-out is an
 * owner ruling, not a preference. D-SCA-10 (2026-09-24): "A tenant adminnak
 * egyébként mindig vissza kell tudni menni a "kezdőfelületre", még akkor is, ha
 * csak egy ügynökjük van és nem lehet több, mert ott tudja kezelni a tagokat."
 * For a caller who can manage the tenant's members `resolveLogoTarget` sends
 * the header logo and breadcrumb to this route; redirecting it into the agent
 * would leave that caller no way back to the home screen - exactly the case
 * D-SCA-10 names. So the members manager keeps the chooser here, asked through
 * the SAME `MEMBERS_CANDIDATE` predicate the logo asks, while their menu entry
 * still goes straight into the agent (the 2026-10-05 instruction is about the
 * menu entry). Every other caller under the condition never sees a one-item
 * chooser: the replace leaves no history entry, so Back never loops into it.
 */
export function resolveChooserRedirect(inputs: AgentsMenuInputs): string | null {
  if (inputs.usable(MEMBERS_CANDIDATE)) return null;
  return resolveSingleAgentTarget(inputs);
}

/**
 * Where the header logo (and the workspace breadcrumb beside it) leads THIS
 * caller: an ABSOLUTE path, or `null` for no link (RULE-005(b)).
 *
 * D-TSG-16 (owner ruling 2026-09-23, "Választó, ha több ügynök"): the chooser
 * ONLY when the caller can use more than one agent; with exactly one agent the
 * logo goes inside it. This is not the landing chain above - the logo does not
 * read the landing preference - but it shares its agent list and its
 * `insideAgent` test, so an only agent that is gone or has no usable surface is
 * never a target. D-SCA-09 (2026-09-24) confirmed the "otherwise" legs of 2.
 *
 * D-SCA-10 (owner ruling 2026-09-24, amends D-TSG-16 and D-SCA-09): "A tenant
 * adminnak ... mindig vissza kell tudni menni a kezdőfelületre, még akkor is, ha
 * csak egy ügynökjük van ..., mert ott tudja kezelni a tagokat." Inside an agent
 * the header offers only "My settings" and logout, so a caller who can manage the
 * tenant's members skips leg 1: the members page is reached from the workspace
 * layout, never from the agent shell. "Can manage the members" is the predicate
 * over `MEMBERS_CANDIDATE` - the page's permission and resolved surface - so a
 * tenant that hid the members page for this caller took the reason away and leg 1
 * applies again.
 *
 *  1. Exactly one agent in the list, and the caller cannot manage the members ->
 *     inside it, when it has a usable child.
 *  2. Otherwise (a members manager, zero agents, two or more, a list still
 *     unresolved, or an only agent with nothing usable) -> the chooser when
 *     usable, else the first usable workspace-index child, else `null`.
 */
export function resolveLogoTarget(inputs: {
  agents: readonly LandingAgent[] | null;
  usable: UsablePredicate;
  /** Where inside the agent it opens (D-TSG-32); absent or null = the default order. */
  agentStart?: AgentStartConfig | null;
}): string | null {
  const { agents, usable, agentStart } = inputs;
  const managesMembers = usable(MEMBERS_CANDIDATE);
  if (!managesMembers && agents !== null && agents.length === 1) {
    const inside = insideAgent(agents[0].id, agents, usable, agentStart);
    if (inside !== null) return inside;
  }
  if (usable(CHOOSER_CANDIDATE)) return CHOOSER_PATH;
  const child = firstUsableWorkspaceChild({ group: "index", usable });
  return child === null ? null : `/${child}`;
}
